function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIsomorphicState } from '@dkrm/general-libs/IsomorphicState/utils';
import { getCDNImageUrl, getS3ImageName } from '@dkrm/general-libs/Utils/imageUtils';
import { getUrlWithOverridenQueries } from '@dkrm/general-libs/Utils/urlUtils';
import { View } from '@dkrm/ui-kit-basic';
import ModifiedRNWImage from './ModifiedRNWImage';
import { buildSrcSet, isImageUriAbleToUseSmartImage, sanitizePath, shallowCompareProps } from './utils';
const K_EMPTY_ADDITIONAL_QUERY = {};
const Image = /*#__PURE__*/memo(({
  shouldPreloadSmallerVersion = true,
  smallerVersionRatio = 1,
  initialBlur = 12,
  sizes = 'auto',
  width,
  height,
  additionalQuery = {},
  source,
  style,
  ...rest
}) => {
  const isSSR = typeof window === 'undefined';
  const [isError, setIsError] = useState(false);
  const setErrorTrue = useCallback(() => setIsError(true), []);
  const imageUri = useMemo(() => {
    if (source === null || source === undefined) {
      return '';
    }
    if (typeof source === 'string') {
      return source;
    }
    return source.uri || '';
  }, [source]);
  const imagePath = useMemo(() => {
    return getS3ImageName(imageUri);
  }, [imageUri]);
  const mediaCDNParams = useMemo(() => ({
    auto: 'webp',
    optimize: 'medium',
    width,
    height,
    ...additionalQuery
  }), [additionalQuery, height, width]);
  const baseSource = useMemo(() => getCDNImageUrl(sanitizePath(imagePath).replace(/^\//, '')), [imagePath]);
  const wasRenderedInSSR = useIsomorphicState(false, baseSource, isSSR);
  const isPng = useMemo(() => {
    const splittedSource = baseSource.split('.');
    return splittedSource[splittedSource.length - 1] === 'png';
  }, [baseSource]);
  const optimizationParams = useMemo(() => ({
    optimize: 'high',
    quality: 20,
    dpr: smallerVersionRatio,
    ...(isPng ? {
      auto: undefined,
      format: 'png8'
    } : {})
  }), [isPng, smallerVersionRatio]);
  const ssrSource = useMemo(() => {
    return getUrlWithOverridenQueries(baseSource, {
      ...mediaCDNParams,
      ...optimizationParams
    });
  }, [baseSource, mediaCDNParams, optimizationParams]);
  const placeholderSource = useMemo(() => shouldPreloadSmallerVersion ? getUrlWithOverridenQueries(baseSource, {
    ...mediaCDNParams,
    ...optimizationParams,
    blur: `${initialBlur}p`,
    width: mediaCDNParams.width || 200
  }) : ssrSource, [baseSource, initialBlur, mediaCDNParams, optimizationParams, shouldPreloadSmallerVersion, ssrSource]);
  useEffect(() => setIsError(false), [source]);
  if (!isImageUriAbleToUseSmartImage(imageUri)) {
    return /*#__PURE__*/React.createElement(ModifiedRNWImage, _extends({
      defaultSource: imageUri,
      onError: setErrorTrue,
      style: [{
        width,
        height
      }, style]
    }, rest));
  }
  const srcSet = buildSrcSet(imagePath, mediaCDNParams);
  if (isError) return /*#__PURE__*/React.createElement(View, {
    style: style
  });
  if (isSSR) {
    return /*#__PURE__*/React.createElement(ModifiedRNWImage, _extends({
      defaultSource: ssrSource,
      onError: setErrorTrue,
      style: [{
        width,
        height
      }, style]
    }, rest));
  }
  return /*#__PURE__*/React.createElement(ModifiedRNWImage, _extends({
    className: "lazyload",
    "data-sizes": sizes,
    "data-srcset": srcSet,
    defaultSource: wasRenderedInSSR ? ssrSource : placeholderSource,
    onError: setErrorTrue,
    style: [{
      width,
      height
    }, style]
  }, rest));
}, (prevProps, nextProps) => {
  const {
    additionalQuery: prevAdditionalQuery = K_EMPTY_ADDITIONAL_QUERY,
    ...prevPropsWithoutAdditionalQuery
  } = prevProps;
  const {
    additionalQuery: nextAdditionalQuery = K_EMPTY_ADDITIONAL_QUERY,
    ...nextPropsWithoutAdditionalQuery
  } = nextProps;
  return shallowCompareProps(prevPropsWithoutAdditionalQuery, nextPropsWithoutAdditionalQuery) && shallowCompareProps(prevAdditionalQuery, nextAdditionalQuery);
});
Image.displayName = "Image";
export default Image;